"use client"

import type React from "react"
import "../../../styles/admin-buttons.css"

import { useEffect, useState } from "react"
import { Container, Card, Button, Table, Form, Modal, Badge } from "react-bootstrap"
import { useAppDispatch, useAppSelector } from "../../../redux/hooks"
import {
  fetchBusinesses,
  addBusiness,
  updateBusiness,
  deleteBusiness,
  toggleBusinessStatus,
} from "../../../redux/slices/discountsSlice"
import { useTranslation } from "../../../hooks/use-translation"
import { Search, Edit, Trash2, Upload, ToggleLeft, ToggleRight } from "lucide-react"
import { useToast } from "../../../components/ui/use-toast"
import { fetchBuildings } from "../../../redux/slices/buildingsSlice"
import { compareBuildingNames } from "../../../data/buildings"
import { PlusButton } from "../../../components/ui/plus-button"

export default function AdminDiscountsPage() {
  const dispatch = useAppDispatch()
  const { businesses = [], isLoading } = useAppSelector((state) => state.discounts)
  const { buildings = [] } = useAppSelector((state) => state.buildings)
  const { t } = useTranslation()
  const { toast } = useToast()
  const [searchTerm, setSearchTerm] = useState("")
  const [categoryFilter, setCategoryFilter] = useState("all")
  const [buildingFilter, setBuildingFilter] = useState("all")
  const [statusFilter, setStatusFilter] = useState("all") // Added status filter
  const [showModal, setShowModal] = useState(false)
  const [isEditing, setIsEditing] = useState(false)
  const [selectedBusiness, setSelectedBusiness] = useState<any>(null)
  const [formData, setFormData] = useState({
    name: "",
    category: "",
    address: "",
    phone: "",
    discount: "",
    buildings: [] as string[],
    isActive: true,
  })
  const [logo, setLogo] = useState<File | null>(null)
  const [logoPreview, setLogoPreview] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<{ show: boolean; businessId: string; businessName: string }>({
    show: false,
    businessId: "",
    businessName: "",
  })

  useEffect(() => {
    dispatch(fetchBusinesses(categoryFilter !== "all" ? categoryFilter : undefined))
    dispatch(fetchBuildings(false)) // Only fetch active buildings
  }, [dispatch, categoryFilter])

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0]
      setLogo(file)
      setLogoPreview(URL.createObjectURL(file))
    }
  }

  const handleBuildingChange = (buildingId: string) => {
    if (formData.buildings.includes(buildingId)) {
      setFormData({
        ...formData,
        buildings: formData.buildings.filter((b) => b !== buildingId),
      })
    } else {
      setFormData({
        ...formData,
        buildings: [...formData.buildings, buildingId],
      })
    }
  }

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await dispatch(toggleBusinessStatus({ id, isActive: !currentStatus })).unwrap()
      toast({
        title: "Success",
        description: currentStatus
          ? "Business discount disabled successfully"
          : "Business discount enabled successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update business status",
        variant: "destructive",
      })
    }
  }

  const handleCreateBusiness = async () => {
    if (
      !formData.name ||
      !formData.category ||
      !formData.address ||
      !formData.phone ||
      !formData.discount ||
      formData.buildings.length === 0
    ) {
      toast({
        title: "Error",
        description: "Please fill in all fields and select at least one building",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        addBusiness({
          ...formData,
          logo: logo || undefined,
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Business added successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to add business",
        variant: "destructive",
      })
    }
  }

  const handleUpdateBusiness = async () => {
    if (
      !formData.name ||
      !formData.category ||
      !formData.address ||
      !formData.phone ||
      !formData.discount ||
      formData.buildings.length === 0
    ) {
      toast({
        title: "Error",
        description: "Please fill in all fields and select at least one building",
        variant: "destructive",
      })
      return
    }

    try {
      const result = await dispatch(
        updateBusiness({
          id: selectedBusiness.id,
          ...formData,
          logo: logo || undefined,
          isActive: formData.isActive,
        }),
      ).unwrap()

      toast({
        title: "Success",
        description: "Business updated successfully",
      })
      setShowModal(false)
      resetForm()
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update business",
        variant: "destructive",
      })
    }
  }

  const handleDeleteBusiness = async (id: string, name: string) => {
    setDeleteConfirm({
      show: true,
      businessId: id,
      businessName: name,
    })
  }

  const confirmDeleteBusiness = async () => {
    try {
      const result = await dispatch(deleteBusiness(deleteConfirm.businessId)).unwrap()
      toast({
        title: "Success",
        description: "Business deleted successfully",
      })
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete business",
        variant: "destructive",
      })
    } finally {
      setDeleteConfirm({ show: false, businessId: "", businessName: "" })
    }
  }

  const handleEditBusiness = (business: any) => {
    setSelectedBusiness(business)
    setFormData({
      name: business.name,
      category: business.category,
      address: business.address,
      phone: business.phone,
      discount: business.discount,
      buildings: business.buildings || [],
      isActive: business.isActive !== undefined ? business.isActive : true,
    })
    setLogoPreview(business.logo || null)
    setLogo(null)
    setIsEditing(true)
    setShowModal(true)
  }

  const resetForm = () => {
    setFormData({
      name: "",
      category: "",
      address: "",
      phone: "",
      discount: "",
      buildings: [],
      isActive: true,
    })
    setLogo(null)
    setLogoPreview(null)
    setIsEditing(false)
    setSelectedBusiness(null)
  }

  // Filter businesses by search term, category, building, and status
  const filteredBusinesses = businesses
    ? businesses.filter((business) => {
        // Filter by search term with null checks
        const matchesSearch =
          business &&
          ((business.name || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (business.address || "").toLowerCase().includes(searchTerm.toLowerCase()) ||
            (business.discount || "").toLowerCase().includes(searchTerm.toLowerCase()))

        // Filter by category with null check
        const matchesCategory = categoryFilter === "all" || (business && business.category === categoryFilter)

        // Filter by building with null check
        const matchesBuilding =
          buildingFilter === "all" ||
          (business &&
            business.buildings &&
            Array.isArray(business.buildings) &&
            business.buildings.includes(buildingFilter))

        // Filter by status - handle undefined isActive as active (true)
        const isBusinessActive = business.isActive !== false
        const matchesStatus =
          statusFilter === "all" ||
          (statusFilter === "active" && isBusinessActive) ||
          (statusFilter === "disabled" && !isBusinessActive)

        return matchesSearch && matchesCategory && matchesBuilding && matchesStatus
      })
    : []

  return (
    <Container fluid className="py-4 px-2 px-md-4">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h1 className="fs-2 fw-bold mb-0 text-white">{t("discountManagement")}</h1>
        <PlusButton
          onClick={() => {
            resetForm()
            setShowModal(true)
          }}
          title={t("addBusiness")}
        />
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body className="p-4">
          <div className="mb-3">
            <div className="row g-2 mb-3">
              <div className="col-12 col-md-4">
                <Form.Group>
                  <Form.Select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
                    <option value="all">{t("allCategories")}</option>
                    <option value="cafes">{t("cafes")}</option>
                    <option value="restaurants">{t("restaurants")}</option>
                    <option value="gyms">{t("gyms")}</option>
                    <option value="retail">{t("retail")}</option>
                    <option value="services">{t("services")}</option>
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-12 col-md-4">
                <Form.Group>
                  <Form.Select value={buildingFilter} onChange={(e) => setBuildingFilter(e.target.value)}>
                    <option value="all">{t("allBuildings")}</option>
                    {buildings.map((building) => (
                      <option key={building.id} value={building.id}>
                        {building.name}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </div>
              <div className="col-12 col-md-4">
                <Form.Group>
                  <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                    <option value="all">{t("allStatuses")}</option>
                    <option value="active">{t("active")}</option>
                    <option value="disabled">{t("disabled")}</option>
                  </Form.Select>
                </Form.Group>
              </div>
            </div>
            <div className="position-relative">
              <Form.Control
                type="text"
                placeholder={t("searchBusinesses")}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="ps-5"
              />
              <Search className="position-absolute top-50 start-0 translate-middle-y ms-2 text-muted" size={18} />
            </div>
          </div>

          {isLoading ? (
            <div className="text-center py-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : (
            <div className="table-responsive">
              <Table hover className="align-middle">
                <thead className="table-light">
                  <tr>
                    <th className="fw-semibold text-gray-700">{t("name")}</th>
                    <th className="fw-semibold text-gray-700">{t("category")}</th>
                    <th className="fw-semibold text-gray-700">{t("address")}</th>
                    <th className="fw-semibold text-gray-700">{t("discount")}</th>
                    <th className="fw-semibold text-gray-700">{t("buildings")}</th>
                    <th className="fw-semibold text-gray-700">{t("status")}</th>
                    <th className="fw-semibold text-gray-700">{t("actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBusinesses.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        {t("noBusinessesFound")}
                      </td>
                    </tr>
                  ) : (
                    filteredBusinesses.map((business) => (
                      <tr key={business.id} className="border-bottom">
                        <td className="align-middle">
                          <div className="d-flex align-items-center gap-2">
                            {business.logo && (
                              <img
                                src={business.logo || "/placeholder.svg"}
                                alt={business.name}
                                style={{ maxHeight: "32px", maxWidth: "32px", objectFit: "contain", border: "none" }}
                              />
                            )}
                            {business.name}
                          </div>
                        </td>
                        <td className="align-middle">{t(business.category)}</td>
                        <td className="align-middle">{business.address}</td>
                        <td className="align-middle">{business.discount}</td>
                        <td className="align-middle">
                          {business.buildings?.length > 0
                            ? business.buildings
                                .map((buildingId) => buildings.find((b) => b.id === buildingId)?.name || buildingId)
                                .sort(compareBuildingNames)
                                .join(", ")
                            : t("noBuildings")}
                        </td>
                        <td className="align-middle">
                          <Badge bg={business.isActive !== false ? "success" : "secondary"}>
                            {business.isActive !== false ? t("active") : t("disabled")}
                          </Badge>
                        </td>
                        <td className="align-middle">
                          <div className="d-flex gap-2">
                            <Button
                              className={`admin-toggle-btn ${business.isActive !== false ? "active-state" : ""}`}
                              size="sm"
                              onClick={() => handleToggleStatus(business.id, business.isActive !== false)}
                              title={business.isActive !== false ? t("disableBusiness") : t("enableBusiness")}
                            >
                              {business.isActive !== false ? <ToggleRight size={16} /> : <ToggleLeft size={16} />}
                            </Button>
                            <Button
                              className="admin-edit-btn"
                              size="sm"
                              onClick={() => handleEditBusiness(business)}
                              title={t("editBusiness")}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              className="admin-delete-btn"
                              size="sm"
                              onClick={() => handleDeleteBusiness(business.id, business.name)}
                              title={t("deleteBusiness")}
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </Table>
            </div>
          )}
        </Card.Body>
      </Card>

      {/* Create/Edit Business Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{isEditing ? t("editBusiness") : t("addBusiness")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>{t("name")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder={t("enterBusinessName")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("category")}</Form.Label>
              <Form.Select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                <option value="">{t("selectCategory")}</option>
                <option value="cafes">{t("cafes")}</option>
                <option value="restaurants">{t("restaurants")}</option>
                <option value="gyms">{t("gyms")}</option>
                <option value="retail">{t("retail")}</option>
                <option value="services">{t("services")}</option>
              </Form.Select>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("address")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder={t("enterAddress")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("phone")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                placeholder={t("enterPhone")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("discount")}</Form.Label>
              <Form.Control
                type="text"
                value={formData.discount}
                onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
                placeholder={t("enterDiscount")}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>{t("buildings")}</Form.Label>
              <div>
                {buildings.map((building) => (
                  <Form.Check
                    key={building.id}
                    type="checkbox"
                    id={`building-${building.id}`}
                    label={building.name}
                    checked={formData.buildings.includes(building.id)}
                    onChange={() => handleBuildingChange(building.id)}
                    className="mb-2"
                  />
                ))}
              </div>
            </Form.Group>
            {isEditing && (
              <Form.Group className="mb-3">
                <Form.Check
                  type="switch"
                  id="business-active"
                  label={t("businessActive")}
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              </Form.Group>
            )}
            <Form.Group className="mb-3">
              <Form.Label>{t("logo")}</Form.Label>
              <div className="d-flex flex-column gap-2">
                {logoPreview && (
                  <div className="mb-2">
                    <img
                      src={logoPreview || "/placeholder.svg"}
                      alt="Logo Preview"
                      className="img-fluid"
                      style={{ maxHeight: "100px", objectFit: "contain", border: "none" }}
                    />
                  </div>
                )}
                <div className="d-flex align-items-center gap-2">
                  <Button
                    variant="outline-secondary"
                    onClick={() => document.getElementById("logo")?.click()}
                    className="d-flex align-items-center"
                  >
                    <Upload className="size-4 me-2" />
                    {t("selectImage")}
                  </Button>
                  <Form.Control id="logo" type="file" accept="image/*" className="d-none" onChange={handleLogoChange} />
                  {logo && <span className="small text-muted">{logo.name}</span>}
                </div>
              </div>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <button className="modal-cancel-btn" onClick={() => setShowModal(false)}>
            {t("cancel")}
          </button>
          <button className="modal-primary-btn" onClick={isEditing ? handleUpdateBusiness : handleCreateBusiness}>
            {isEditing ? t("update") : t("create")}
          </button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={deleteConfirm.show}
        onHide={() => setDeleteConfirm({ show: false, businessId: "", businessName: "" })}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t("confirmDelete")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t("deleteBusinessConfirmation", { name: deleteConfirm.businessName })}</p>
        </Modal.Body>
        <Modal.Footer>
          <button
            className="modal-cancel-btn"
            onClick={() => setDeleteConfirm({ show: false, businessId: "", businessName: "" })}
          >
            {t("cancel")}
          </button>
          <button className="modal-delete-btn" onClick={confirmDeleteBusiness}>
            {t("delete")}
          </button>
        </Modal.Footer>
      </Modal>
    </Container>
  )
}
